import React, { useMemo } from "react";
import { Box, Button } from "@mui/material";
import downloadFill from "@iconify/icons-eva/download-fill";
import { getIcon } from "src/utils/utility";
import { saveToExcel } from "src/utils/convertAnddownloadCsv";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";

export function isDateTimeField(fieldName) {
  return /(date|time|createdAt|createAt|createdOn|createOn|updateAt|updatedAt|updateOn|updatedOn)/gim.test(
    fieldName
  );
}

export function isNumericField(fieldName) {
  return /(amount|paid|charged|refund|kw|kwh|energy|meter|value|price|cost|total|quantity|start_meter_value|stop_meter_value)/i.test(
    fieldName
  );
}

const ExportCSV = ({ rows, columns, csvMorphemes }) => {
  const csvObject = useMemo(() => {
    let csvObject = {};
    csvObject["headers"] = columns.map((column) => column.headerName);
    csvObject["properties"] = columns.map((column) => column.field);

    // Add types for Excel formatting
    csvObject["types"] = columns.map((column) => {
      if (isDateTimeField(column.field)) return "date";
      if (isNumericField(column.field)) return "number";
      return "string";
    });

    csvObject["valuesObjArr"] = rows.map((row) => {
      let rowValue = { ...row };
      columns.forEach((column) => {
        // Format dates
        if (isDateTimeField(column.field)) {
          rowValue = {
            ...rowValue,
            [column.field]: yearFirstDateTimeFormat(row[column.field]),
          };
        }
        // Special handling for transaction numeric fields
        if (isNumericField(column.field)) {
          let numValue;
          // Handle string numbers that might be wrapped in quotes
          if (typeof row[column.field] === "string") {
            numValue = parseFloat(row[column.field].replace(/[^0-9.-]/g, ""));
          } else {
            numValue = parseFloat(row[column.field]);
          }
          rowValue = {
            ...rowValue,
            [column.field]: isNaN(numValue) ? 0 : numValue,
          };
        }
        // Handle binary values
        if (
          csvMorphemes === "auth_code" &&
          (column.field === "blocked" || column.field === "in_transaction")
        ) {
          rowValue = { ...rowValue, [column.field]: !!row[column.field] };
        }
        // Handle full name
        if (csvMorphemes === "ev_drivers" && column.field === "fullName") {
          rowValue = {
            ...rowValue,
            [column.field]: `${row.firstName} ${row.lastName}`,
          };
        }
      });
      return rowValue;
    });
    return csvObject;
  }, [rows, columns, csvMorphemes]);

  const handleExport = () => {
    // const csvData = changeToCSV(csvObject);
    saveToExcel(csvObject, csvMorphemes);
    // downloadCSV(csvData, csvMorphemes);
  };

  return (
    <Box
      sx={{
        display: "inline-block",
        position: "absolute",
        right: "40px",
        top: "50%",
        transform: "translateY(-50%)",
      }}
    >
      <Button
        variant="contained"
        size="medium"
        startIcon={getIcon(downloadFill)}
        onClick={handleExport}
        sx={{
          background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
          boxShadow: '0 4px 14px 0 rgba(46, 125, 50, 0.39)',
          fontWeight: 700,
          letterSpacing: '0.5px',
          px: 3,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
            transition: 'left 0.5s ease-in-out',
          },
          '&:hover': {
            background: 'linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%)',
            boxShadow: '0 6px 20px 0 rgba(46, 125, 50, 0.5)',
            transform: 'translateY(-2px)',
            '&:before': {
              left: '100%',
            },
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
            color: 'text.disabled',
            boxShadow: 'none',
          },
        }}
      >
        Export To Excel
      </Button>
    </Box>
  );
};

export default ExportCSV;
